/**
 * "Arrange script" preview + apply (F-60, scoped to the F-28 Original Script
 * column).
 *
 * The dialog lists, per selected multi-sentence cell, the pieces the split will
 * create — READ-ONLY text (the words are the script; nothing here can edit
 * them). Each boundary carries a toggle: clearing it merges the two sentences
 * back into one piece (F-60.4's one-click merge for mis-splits), preserving
 * their original separator exactly (segment.ts). Apply runs `applyArrange`
 * inside one `session.batch` — one transaction, one sync unit, one undo step
 * (F-60.5); cells edited or deleted while the preview sat open are skipped and
 * reported (C6/C4).
 *
 * AI ASSIST: a cell that is ONE long sentence with no punctuation to split on
 * (`Intl.Segmenter` found nothing) is offered a SEPARATE, explicit "Suggest AI
 * split" action — never fired automatically on open, so opening the dialog
 * never spends anything by itself (F-70's translate button sets the same
 * precedent). The server never returns replacement text, only cut OFFSETS
 * into the exact string the client sent (ai-suggest.ts / segment.ts
 * `rawFromOffsets`), so an AI-sourced entry has the identical word-preservation
 * guarantee as a pure-logic one and renders in the SAME list with the SAME
 * merge toggles — the dialog does not know or care which path produced it.
 */
import { useEffect, useMemo, useState } from "react";
import { useStore } from "zustand";

import { suggestArrangeSplit } from "../../core/api.js";
import { newId } from "../../core/ids.js";
import type { BoardSession } from "../../core/session.js";
import { Modal } from "../../ui/Modal.js";
import { showToast } from "../../ui/toast.js";
import { isAiEligible, selectAiBatch, type AiCandidate } from "./ai-suggest.js";
import { applyArrange, type ArrangeCellPlan, type ArrangeResult } from "./arrange.js";
import { piecesForBreaks, rawFromOffsets, segmentClauses, segmentSentences } from "./segment.js";
import { arrangeUi, type ArrangeTarget } from "./store.js";
// Side-effect import: registers the F-27 arrange action (idempotent).
import "./action.js";

/**
 * Preview cap: cells beyond this many selected rows are left untouched (the
 * user re-runs on the remainder). Segmenting the text of an unbounded
 * selection — a full 10k-row column click — inside one dialog open is real
 * work, and a preview listing thousands of cells is unreadable anyway.
 */
export const MAX_ARRANGE_CELLS = 200;

export interface ArrangeLayerProps {
  session: BoardSession;
}

export function ArrangeLayer({ session }: ArrangeLayerProps) {
  const dialog = useStore(arrangeUi, (s) => s.dialog);

  // A dialog must never survive leaving the board it names.
  useEffect(() => {
    return () => arrangeUi.getState().closeDialog();
  }, []);

  const forThisBoard = dialog !== null && dialog.boardId === session.boardId;
  return forThisBoard ? <ArrangeDialog session={session} target={dialog} /> : null;
}

/** One splittable cell in the preview: its raw segments + boundary choices. */
interface PreviewEntry {
  rowId: string;
  sourceText: string;
  raw: readonly string[];
  /** breaks[i] = split after segment i. All true initially. */
  breaks: readonly boolean[];
}

function buildEntries(
  session: BoardSession,
  target: ArrangeTarget,
): { entries: PreviewEntry[]; truncated: number; aiEligible: AiCandidate[] } {
  const { board } = session;
  const entries: PreviewEntry[] = [];
  const aiEligible: AiCandidate[] = [];
  const rowIds = target.rowIds.slice(0, MAX_ARRANGE_CELLS);
  for (const rowId of rowIds) {
    const text = board.getCellText(target.sheetId, rowId, target.colId);
    if (text.trim().length === 0) continue;
    // One row per sentence, then a long sentence split further at its clause
    // marks (commas etc.) — both deterministic, both exact-partition, so the
    // preview treats a clause cut exactly like a sentence cut.
    const raw = segmentSentences(text).flatMap((segment) => segmentClauses(segment));
    if (raw.length < 2) {
      // Nothing for the pure-logic paths to split — offer it to the AI path
      // only when it is long enough that leaving it as one row is a problem.
      if (isAiEligible(raw, text)) aiEligible.push({ rowId, text });
      continue;
    }
    entries.push({
      rowId,
      sourceText: text,
      raw,
      breaks: raw.slice(1).map(() => true),
    });
  }
  return { entries, truncated: Math.max(0, target.rowIds.length - rowIds.length), aiEligible };
}

function outcomeMessage(result: ArrangeResult): string {
  const parts: string[] = [];
  parts.push(
    result.arranged === 0
      ? "Nothing was arranged"
      : `Arranged ${result.arranged} cell${result.arranged === 1 ? "" : "s"} into ${
          result.arranged + result.newRows
        } rows`,
  );
  if (result.skipped > 0) {
    parts.push(`${result.skipped} skipped (changed or deleted meanwhile — re-open to retry)`);
  }
  return parts.join(" · ");
}

function ArrangeDialog({ session, target }: { session: BoardSession; target: ArrangeTarget }) {
  const close = () => arrangeUi.getState().closeDialog();

  // Computed ONCE at open (the preview the user is confirming); the C6 hash
  // guard in applyArrange covers anything that changes underneath it.
  const [initial] = useState(() => buildEntries(session, target));
  const [entries, setEntries] = useState<PreviewEntry[]>(initial.entries);

  // AI-assist state — every cell here has NO pure-logic split; the button
  // below is the only thing that ever calls the server. `aiPending` shrinks
  // as batches are attempted (selectAiBatch may leave some for a follow-up
  // click); resolved cells move to `entries` (a real split) or `aiNoSplit`
  // (the model found nothing confident).
  const [aiPending, setAiPending] = useState<AiCandidate[]>(initial.aiEligible);
  const [aiNoSplit, setAiNoSplit] = useState<string[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const toggleBreak = (entryIndex: number, breakIndex: number) => {
    setEntries((prev) =>
      prev.map((entry, i) =>
        i === entryIndex
          ? {
              ...entry,
              breaks: entry.breaks.map((b, j) => (j === breakIndex ? !b : b)),
            }
          : entry,
      ),
    );
  };

  const suggestAi = async () => {
    const { batch } = selectAiBatch(aiPending);
    if (batch.length === 0) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const response = await suggestArrangeSplit(
        session.boardId,
        batch.map((candidate) => ({ id: candidate.rowId, text: candidate.text })),
      );
      const breaksById = new Map(response.suggestions.map((s) => [s.id, s.breaks]));
      const newEntries: PreviewEntry[] = [];
      const newNoSplit: string[] = [];
      for (const candidate of batch) {
        const breaks = breaksById.get(candidate.rowId) ?? [];
        if (breaks.length === 0) {
          newNoSplit.push(candidate.rowId);
          continue;
        }
        const raw = rawFromOffsets(candidate.text, breaks);
        newEntries.push({
          rowId: candidate.rowId,
          sourceText: candidate.text,
          raw,
          breaks: raw.slice(1).map(() => true),
        });
      }
      setEntries((prev) => [...prev, ...newEntries]);
      setAiNoSplit((prev) => [...prev, ...newNoSplit]);
      const attempted = new Set(batch.map((candidate) => candidate.rowId));
      setAiPending((prev) => prev.filter((candidate) => !attempted.has(candidate.rowId)));
    } catch (err) {
      setAiError(err instanceof Error ? err.message : "AI suggestion failed");
    } finally {
      setAiLoading(false);
    }
  };

  const plans = useMemo<ArrangeCellPlan[]>(
    () =>
      entries
        .map((entry) => ({
          rowId: entry.rowId,
          pieces: piecesForBreaks(entry.raw, entry.breaks),
          sourceText: entry.sourceText,
        }))
        .filter((plan) => plan.pieces.length >= 2),
    [entries],
  );
  const newRowCount = plans.reduce((sum, plan) => sum + plan.pieces.length - 1, 0);

  const apply = () => {
    let result: ArrangeResult | null = null;
    session.batch(() => {
      result = applyArrange(
        session.board,
        target.sheetId,
        target.colId,
        plans,
        () => `r${newId(10)}`,
      );
    });
    // F-45: while frozen, session.batch never runs its callback — report that
    // honestly instead of pretending the split happened.
    showToast(result === null ? "Offline — nothing was changed" : outcomeMessage(result));
    close();
  };

  const aiBatchPreview = selectAiBatch(aiPending);
  const nothingAtAll = entries.length === 0 && aiPending.length === 0 && aiNoSplit.length === 0;

  return (
    <Modal title="Arrange script" onClose={close}>
      <div className="arrange-dialog">
        {nothingAtAll ? (
          <p className="arrange-empty">
            Every selected Original Script cell is already a single sentence — nothing to
            arrange.
          </p>
        ) : (
          <>
            {entries.length > 0 && (
              <>
                <p className="arrange-note">
                  Long cells split into <strong>one sentence per row</strong> — and a long
                  sentence splits further at its commas — inserted directly below. Words are
                  never changed; content in other columns stays with the original row. Click a
                  scissors mark to merge a mis-split boundary.
                </p>
                <div className="arrange-list">
                  {entries.map((entry, entryIndex) => {
                    const pieces = piecesForBreaks(entry.raw, entry.breaks);
                    return (
                      <div key={entry.rowId} className="arrange-cell">
                        <div className="arrange-cell-title">
                          Cell → {pieces.length} row{pieces.length === 1 ? "" : "s"}
                        </div>
                        <div className="arrange-sentences">
                          {entry.raw.map((segment, i) => (
                            <span key={i}>
                              <span className="arrange-sentence">{segment.trim()}</span>
                              {i < entry.raw.length - 1 && (
                                <button
                                  type="button"
                                  className={`arrange-break${
                                    entry.breaks[i] === true ? " on" : ""
                                  }`}
                                  data-tip={
                                    entry.breaks[i] === true
                                      ? "Splitting here — click to merge these sentences"
                                      : "Merged — click to split here again"
                                  }
                                  aria-pressed={entry.breaks[i] === true}
                                  onClick={() => toggleBreak(entryIndex, i)}
                                >
                                  {entry.breaks[i] === true ? "✂" : "⊕"}
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {(aiPending.length > 0 || aiNoSplit.length > 0 || aiError !== null) && (
              <div className="arrange-ai">
                {aiPending.length > 0 && (
                  <>
                    <p className="arrange-ai-note">
                      {aiPending.length} cell{aiPending.length === 1 ? " has" : "s have"} no
                      punctuation to split on but {aiPending.length === 1 ? "is" : "are"} too
                      long for one row. AI can suggest where to cut — it never changes a word,
                      only where the cuts fall.
                    </p>
                    <button
                      type="button"
                      className="btn"
                      disabled={aiLoading}
                      onClick={() => void suggestAi()}
                    >
                      {aiLoading
                        ? "Asking AI…"
                        : `Suggest AI split (${aiBatchPreview.batch.length}${
                            aiBatchPreview.skipped > 0 ? ` of ${aiPending.length}` : ""
                          })`}
                    </button>
                  </>
                )}
                {aiNoSplit.length > 0 && (
                  <p className="arrange-ai-note">
                    AI found no confident place to cut {aiNoSplit.length} cell
                    {aiNoSplit.length === 1 ? "" : "s"} — left as a single row.
                  </p>
                )}
                {aiError !== null && (
                  <p className="arrange-error" role="alert">
                    {aiError}
                  </p>
                )}
              </div>
            )}
            {initial.truncated > 0 && (
              <p className="arrange-truncated">
                Showing the first {MAX_ARRANGE_CELLS} selected rows — {initial.truncated} more
                were not examined. Run Arrange again on the rest afterwards.
              </p>
            )}
          </>
        )}
        <div className="modal-actions">
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={plans.length === 0}
            onClick={apply}
          >
            {plans.length === 0
              ? "Nothing to arrange"
              : `Arrange ${plans.length} cell${plans.length === 1 ? "" : "s"} (+${newRowCount} row${
                  newRowCount === 1 ? "" : "s"
                })`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
